import { createReducer, on, Action } from '@ngrx/store';
import {
	UserListArgs,
	PagedResultsOfUser,
	User,
	PagedResultsOfUserRegistrationRequest,
} from 'src/shared/api';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import * as CustomerUsersActions from './customer-users.actions';

export const customerUsersFeatureKey = 'customer-users';

export interface CustomerUsersState extends EntityState<User> {
	selectedUserId?: string;
	isListInitialized: boolean;
	isRegistrationRequestListInitialized: boolean;
	isRegistrationRequestListLoading?: boolean;
	registrationRequestPagedResults?: PagedResultsOfUserRegistrationRequest;
	isDetailInitialized: boolean;
	isListLoading?: boolean;
	isSaving?: boolean;
	isDetailLoading?: boolean;
	listMessage?: string;
	listArgs?: UserListArgs;
	pagedResults?: PagedResultsOfUser;
	shouldStayOnPage?: boolean;
	error?: any;
}

export const customerUserAdapter: EntityAdapter<User> = createEntityAdapter<
	User
>();

export interface CustomerUsersPartialState {
	readonly [customerUsersFeatureKey]: CustomerUsersState;
}

export const initialState: CustomerUsersState = customerUserAdapter.getInitialState(
	{
		isListInitialized: false,
		isDetailInitialized: false,
		isRegistrationRequestListInitialized: false,
		// additional entity state properties
	}
);

const customerUsersReducer = createReducer(
	initialState,
	on(CustomerUsersActions.filtersUpdated, (state, { filters }) => ({
		...state,
		listArgs: filters,
		isListLoading: true,
		isListInitialized: true,
	})),
	on(
		CustomerUsersActions.pagedCustomerUsersLoaded,
		(state, { pagedCustomerUsers, listArgs }) => ({
			...state,
			listArgs,
			pagedResults: pagedCustomerUsers,
			isListLoading: false,
			isListInitialized: true,
		})
	),
	on(CustomerUsersActions.errorLoadingCustomerUsers, (state, { error }) => ({
		...state,
		error,
		isListLoading: false,
		isListInitialized: true,
	})),
	on(CustomerUsersActions.customerUsersListDestroyed, (state) => ({
		...state,
		pagesResults: { resultCount: 0, results: [], pageNumber: 0 },
		isListInitialized: false,
	})),
	on(CustomerUsersActions.saveRequested, (state, { shouldStayOnPage }) => ({
		...state,
		isSaving: true,
		shouldStayOnPage,
	})),
	on(CustomerUsersActions.newCustomerUserSaved, (state) =>
		customerUserAdapter.removeOne('', { ...state })
	),
	on(CustomerUsersActions.newCustomerUserSaved, (state, { user }) =>
		customerUserAdapter.addOne(user, { ...state, isSaving: false })
	),
	on(CustomerUsersActions.customerUserSaved, (state, { user }) =>
		customerUserAdapter.updateOne(
			{
				id: user.id,
				changes: {
					...user,
				},
			},
			{ ...state, isSaving: false }
		)
	),
	on(
		CustomerUsersActions.customerUserSelected,
		(state, { selectedUserId }) => ({
			...state,
			selectedUserId,
		})
	),
	on(CustomerUsersActions.customerUserLoadedFromStore, (state) => ({
		...state,
		isDetailLoading: false,
		isDetailInitialized: true,
		isSaving: false,
	})),
	on(CustomerUsersActions.customerUserLoadedFromApi, (state, { user }) =>
		customerUserAdapter.upsertOne(user, {
			...state,
			isDetailLoading: false,
			isDetailInitialized: true,
			isSaving: false,
		})
	),
	on(CustomerUsersActions.errorSavingCustomerUser, (state, { error }) => ({
		...state,
		error,
		isSaving: false,
	})),
	on(CustomerUsersActions.registrationRequestSelected, (state) =>
		customerUserAdapter.addOne(
			{ id: '' },
			{
				...state,
				selectedUserId: '',
				isDetailInitialized: false,
				isDetailLoading: true,
			}
		)
	),
	on(CustomerUsersActions.pagedRegistrationRequestsRequested, (state) => ({
		...state,
		isRegistrationRequestListLoading: true,
	})),
	on(
		CustomerUsersActions.pagedRegistrationRequestsLoaded,
		(state, { pagedRegistrationRequests }) => ({
			...state,
			registrationRequestPagedResults: pagedRegistrationRequests,
			isRegistrationRequestListLoading: false,
			isRegistrationRequestListInitialized: true,
		})
	),
	on(
		CustomerUsersActions.registrationRequestDenyCompleted,
		(state, { registrationRequest }) => ({
			...state,
			registrationRequestPagedResults: {
				...state.registrationRequestPagedResults,
				results: state.registrationRequestPagedResults.results.map((item) => {
					if (item.id === registrationRequest.id) {
						return { ...registrationRequest };
					}
					return item;
				})
			}
		})
	),
	on(CustomerUsersActions.customerUserDetailDestroyed, (state) => ({
		...state,
		isDetailInitialized: undefined,
		isDetailLoading: undefined,
		isLoadingLogo: true,
		payrollType: undefined,
		error: undefined,
	}))
);

export function reducer(state: CustomerUsersState | undefined, action: Action) {
	return customerUsersReducer(state, action);
}
